import {Injectable} from '@angular/core';
import {ApibaseService} from './apibase.service';
import {asyncScheduler, BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {catchError, map, observeOn, retryWhen} from 'rxjs/operators';
import {emptyRoom, RoomVO} from '~model/vo/room-vo';
import {ChatMessageListVO, ChatMessageVO, emptyChatMessage, emptyChatMessageList} from '~model/vo/chat-message-vo';
import {ResultMessageVo} from '~model/vo/result-message-vo';
import {genericRetryStrategy} from '~model/rxjsPipes/baseEstrategy';
import { HttpClient } from '@angular/common/http';
import {AbstractMqttService} from '~core/api/mqtt/abstract-mqtt.service';

@Injectable()
export class ChatService extends ApibaseService {

  prefixoUrl = 'controlTower';
  urlFunction = 'ms-chat';

  activeChatRooms$: BehaviorSubject<RoomVO[]> = new BehaviorSubject<RoomVO[]>([]);
  selectedRoom$: BehaviorSubject<RoomVO> = new BehaviorSubject<RoomVO>(emptyRoom());

  notificationsTopic$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  currentUsername$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  chatMessages$: BehaviorSubject<ChatMessagesObject>
    = new BehaviorSubject<ChatMessagesObject>({
    allMessages: {},
    unreadMessages: {},
  });

  pageSize = 30;

  notificationsSubscription!: Subscription;
  private subscriptions: Subscription[] = [];

  constructor(
    protected http: HttpClient,
    protected eventMqttService: AbstractMqttService
  ) {
    super(http);
  }

  inicializar(): void {
    this.updateChatRoomsList();

    const sub = this.currentUsername$.subscribe(_ => this.updateChatRoomsList());
    this.subscriptions.push(sub);
  }

  limpar(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.activeChatRooms$.next([]);
    this.selectedRoom$.next(emptyRoom());
    this.chatMessages$.next({ allMessages: {}, unreadMessages: {} });
  }

  updateOnlineStatus(): Observable<{ onlineTopic: string }> {
    return super.patch('', {extraUrl: `users/online`, objEnvio: {instant: new Date().getTime()}});
  }

  getActiveChatRooms(): Observable<RoomVO[]> {
    return super.get<RoomVO[]>({extraUrl: `attendant/rooms/active`});
  }

  getChatRoomsHistory(): Observable<RoomVO[]> {
    return super.get<RoomVO[]>({extraUrl: `attendant/rooms/history`});
  }

  updateChatRoomsList(): void {
    this.getActiveChatRooms().pipe(observeOn(asyncScheduler), retryWhen(genericRetryStrategy({
      scalingDuration: 2000,
      excludedStatusCodes: this.errorCodes,
      maxRetryAttempts: 0,
    }))).subscribe(rooms => {
      this.updateOnlineStatus().subscribe(({onlineTopic}) => {
        this.notificationsTopic$.next(onlineTopic);
        rooms.unshift({...emptyRoom(), topic: onlineTopic});

        this.activeChatRooms$.next(rooms);

        for (const {id, topic} of rooms) {
          if (id) {
            this.updateChatRoomMessages(id);

            this.eventMqttService.subscribe(topic, res => {
              this.updateOnlineStatus().subscribe();

              const vo: TopicRoomVO = JSON.parse(res.payload.toString());

              if (vo.type === 'NEW_MESSAGES' && vo.senderUsername.split('@')[0] !== this.currentUsername$.value) {
                this.updateChatRoomMessages(id, {newMessage: true});
              }
            });
          }
        }

        if (!this.notificationsSubscription) {
          this.activateMqttService();
        }
      });
    });
  }

  activateMqttService(): void {
    this.notificationsSubscription = this.eventMqttService.subscribe(this.notificationsTopic$.value, res => {
      this.updateOnlineStatus().subscribe(_ => {
        const mqttItem = JSON.parse(res.payload.toString());

        const chatMessages = this.chatMessages$.value;

        switch (mqttItem.type) {
          case 'ROOM_CLOSED':
            this.eventMqttService.unsubscribe(mqttItem.roomId);
            let newChatRooms = this.activeChatRooms$.value;
            newChatRooms = newChatRooms.filter(chat => chat.id !== mqttItem.roomId);
            this.activeChatRooms$.next(newChatRooms);

            chatMessages.allMessages[mqttItem.roomId] = emptyChatMessageList();
            chatMessages.unreadMessages[mqttItem.roomId] = emptyChatMessageList();
            this.chatMessages$.next(chatMessages);

            if (this.selectedRoom$.value.id === mqttItem.roomId) {
              this.selectedRoom$.next(this.activeChatRooms$.value.length > 0 ? this.activeChatRooms$.value[0] : emptyRoom());
            }
            break;
          case 'INVITE_LINK_ROOM':
            this.clearInvite(mqttItem.roomId);
            if (!chatMessages.unreadMessages['']) {
              chatMessages.unreadMessages[''] = emptyChatMessageList();
            }

            const inviteMessage = {
              ...emptyChatMessage(),
              receiver: this.currentUsername$.value,
              instant: new Date().getTime(),
              inviteInfo: {
                id: mqttItem.roomId,
                clientName: mqttItem.clientName,
                clientUsername: mqttItem.clientUsername,
              },
            };

            if (!chatMessages.unreadMessages['']) {
              chatMessages.unreadMessages[''] = emptyChatMessageList();
            }

            chatMessages.unreadMessages[''].chats.content.push(inviteMessage);
            chatMessages.unreadMessages[''].chats.total = chatMessages.unreadMessages[''].chats.total + 1;

            if (!chatMessages.allMessages['']) {
              chatMessages.allMessages[''] = emptyChatMessageList();
            }

            chatMessages.allMessages[''].chats.content.push(inviteMessage);
            chatMessages.allMessages[''].chats.total = chatMessages.allMessages[''].chats.total + 1;

            this.chatMessages$.next(chatMessages);
            break;
          case 'CONFIRM_LINK_ROOM':
            this.clearInvite(mqttItem.roomId);
            this.updateChatRoomsList();
            break;
          case 'CANCEL_INVITE_LINK_ROOM':
            this.clearInvite(mqttItem.roomId);
            this.updateChatRoomsList();
        }
      });
    });
  }

  acceptChatRoomInvite(chatRoomId: string): Observable<ResultMessageVo> {
    return super.patch('',
      {
        extraUrl: `attendant/rooms/${chatRoomId}/accept`,
        objEnvio: {instant: new Date().getTime()},
      }).pipe(
      map(_ => ({message: chatRoomId})),
      catchError((err) => {
        const erro = {erro: true, message: 'Ocorreu um erro inesperado na operação'};
        if (err.status === 400) {
          erro.message = 'Essa operação não foi permitida';
        }
        return of(erro);
      })
    );
  }

  refuseChatRoomInvite(chatRoomId: string): Observable<ResultMessageVo> {
    return super.patch('',
      {extraUrl: `attendant/rooms/${chatRoomId}/refuse`}).pipe(
      map(_ => ({message: chatRoomId})),
      catchError((err) => {
        const erro = {erro: true, message: 'Ocorreu um erro inesperado na operação'};
        if (err.status === 400) {
          erro.message = 'Essa operação não foi permitida';
        }
        return of(erro);
      })
    );
  }

  clearInvite(chatRoomId: string): void {
    const chatMessages = this.chatMessages$.value;

    if (!chatMessages.allMessages['']) {
      chatMessages.allMessages[''] = emptyChatMessageList();
    }

    if (!chatMessages.unreadMessages['']) {
      chatMessages.unreadMessages[''] = emptyChatMessageList();
    }

    const allChats = chatMessages.allMessages[''].chats;
    const allChatsTotal = allChats.total;

    const unreadChats = chatMessages.unreadMessages[''].chats;
    const unreadChatsTotal = unreadChats.total;

    allChats.content = allChats.content.filter(m => m.inviteInfo?.id !== chatRoomId);
    allChats.total = allChats.content.length;

    unreadChats.content = unreadChats.content.filter(m => m.inviteInfo?.id !== chatRoomId);
    unreadChats.total = unreadChats.content.length;

    if (chatMessages !== this.chatMessages$.value || allChatsTotal !== allChats.total || unreadChatsTotal !== unreadChats.total) {
      this.chatMessages$.next(chatMessages);
    }
  }

  updateChatRoomMessages(chatRoomId: string, options: { offset?: number, mostRecent?: boolean, newMessage?: boolean } = {}): void {
    this.updateOnlineStatus().subscribe({
      next: _ => {
        this.getUnreadMessagesInRoom(chatRoomId).subscribe({
          next: unreadMessages => {
            let descending = false;
            const chatMessages = this.chatMessages$.value;

            const list = chatMessages.allMessages[chatRoomId] || emptyChatMessageList();

            if (!options.offset && options.offset !== 0) {
              options.offset = list.chats.offset;
            }

            if (!list.notFirstLoad || list.chats.total - list.chats.offset <= this.pageSize && options.newMessage) {
              options.mostRecent = true;
            }

            if (options.mostRecent) {
              const newMessages = unreadMessages.chats.total;
              options.offset = this.pageSize * Math.floor(newMessages / this.pageSize);
              descending = true;
            } else if (options.offset < 0) {
              options.offset = 0;
            }

            this.getAllMessagesInRoom(chatRoomId, {descending, offset: options.offset}).subscribe({
              next: allMessages => {
                if (!allMessages.chats.content) {
                  allMessages.chats.content = [];
                }
                if (!unreadMessages.chats.content) {
                  unreadMessages.chats.content = [];
                }

                if (descending === true) {
                  allMessages.chats.content = allMessages.chats.content.reverse();
                  allMessages.chats.offset = allMessages.chats.total - this.pageSize - allMessages.chats.offset;
                }

                allMessages.notFirstLoad = true;
                chatMessages.allMessages[chatRoomId] = allMessages;
                chatMessages.unreadMessages[chatRoomId] = unreadMessages;
                this.chatMessages$.next(chatMessages);
              },
            });
          },
        });
      },
    });
  }

  getAllMessagesInRoom(roomId: string, options: { descending?: boolean, offset?: number } = {}): Observable<ChatMessageListVO> {

    const sort = options.descending ? 'DESC' : 'ASC';

    const extraUrl = `rooms/${roomId}/chats?direction=${sort}&limit=${this.pageSize}&offset=${options.offset || 0}&chatBy=HISTORY`;
    return super.get<ChatMessageListVO>({extraUrl}).pipe(
      retryWhen(genericRetryStrategy({
        scalingDuration: 2000,
        excludedStatusCodes: this.errorCodes,
        maxRetryAttempts: 0,
      }))
    );
  }

  getUnreadMessagesInRoom(roomId: string): Observable<ChatMessageListVO> {
    return super.get<ChatMessageListVO>({extraUrl: `rooms/${roomId}/chats/?offset=0&chatBy=NOT_VIEWED`}).pipe(
      retryWhen(genericRetryStrategy({
        scalingDuration: 2000,
        excludedStatusCodes: this.errorCodes,
        maxRetryAttempts: 0,
      }))
    );
  }

  sendMessage(roomId: string, sentMessage: ChatMessageVO): Observable<ChatMessageVO> {
    const objEnvio: ChatMessageVO = {
      content: sentMessage.content,
      instant: sentMessage.instant,
      receiver: sentMessage.receiver,
      receiverHash: sentMessage.receiverHash,
    };

    return super.post({extraUrl: `rooms/${roomId}/chats`, objEnvio});
  }

  markAsRead(ids: string[]): Observable<ResultMessageVo> {
    return super.patch('', {
      extraUrl: `rooms/${this.selectedRoom$.value.id}/chats/viewed`, objEnvio: {
        ids,
        instant: new Date().getTime(),
      },
    }).pipe(
      map(_ => ({message: 'Mensagens marcadas como lidas'})),
      catchError((err) => {
        const erro = {erro: true, message: 'Ocorreu um erro inesperado na operação'};
        if (err.status === 400) {
          erro.message = 'Essa operação não foi permitida';
        }
        return of(erro);
      })
    );
  }
}

interface ChatMessagesObject {
  allMessages: { [chatRoomId: string]: ChatMessageListVO };
  unreadMessages: { [chatRoomId: string]: ChatMessageListVO };
}

interface TopicRoomVO {
  type: string;
  senderUsername: string;
}
